/**
 * Acompanhamento de Compras: filtros da fila, pendências e indicadores operacionais.
 * Só leitura: nada aqui muda situações, aprovações ou permissões.
 */
import { z } from 'zod';
import {
  CLOSED_PURCHASE_STATUSES,
  PURCHASE_PRIORITIES,
  PURCHASE_STATUSES,
  PURCHASE_TYPES,
  purchaseNumber,
  type PurchaseStatus,
} from './purchasing.ts';
import { costCenterScopeFields } from './cost-center.ts';
import type { SlaLight } from './sla-clock.ts';
import { uuid } from './schemas.ts';

const DAY_MS = 86_400_000;

/** Filtro de situação da fila: abertas, encerradas, todas ou uma situação. */
export const purchaseQueueStatus = z
  .union([z.enum(['open', 'closed', 'all']), z.enum(PURCHASE_STATUSES)])
  .default('open');
export type PurchaseQueueStatus = z.infer<typeof purchaseQueueStatus>;

export const PURCHASE_QUEUE_OWNERS = ['all', 'me', 'none'] as const;
export type PurchaseQueueOwner = (typeof PURCHASE_QUEUE_OWNERS)[number];

/** Filtros da fila aplicados depois da busca por situação (na tela e na exportação). */
export const purchaseQueueFilters = z.object({
  owner: z.enum(PURCHASE_QUEUE_OWNERS).default('all'),
  departmentId: uuid.nullish(),
  priority: z.enum(PURCHASE_PRIORITIES).nullish(),
  overdue: z.boolean().default(false),
  search: z.string().trim().max(200).default(''),
  // Recorte por centros de custo (ADR 0047): "Meus centros", por Master ou por Gerencial.
  ...costCenterScopeFields,
});
export type PurchaseQueueFilters = z.infer<typeof purchaseQueueFilters>;

/** Etapas da compra em processamento, na ordem do caminho (só leitura das situações reais). */
export const PURCHASE_STAGES = [
  'need_approval',
  'triage',
  'quoting',
  'contracting_approval',
  'contract',
  'order',
  'receiving',
] as const;
export type PurchaseStage = (typeof PURCHASE_STAGES)[number];

/**
 * Recortes da fila que os indicadores da Visão geral abrem. O número do cartão e a lista saem do
 * mesmo cálculo no servidor, então não têm como divergir.
 */
export const PURCHASE_QUEUE_INDICATORS = [
  'attendance_in',
  'attendance_out',
  'attendance_none',
  'approval_in',
  'approval_out',
  'approval_none',
  'approval_all',
  'awaiting_reply',
  // Em processamento (ADR 0102): todas as abertas, por tipo e por etapa.
  'in_progress',
  ...PURCHASE_TYPES.map((t) => `type_${t}` as const),
  ...PURCHASE_STAGES.map((s) => `stage_${s}` as const),
] as const;
export type PurchaseQueueIndicator = (typeof PURCHASE_QUEUE_INDICATORS)[number];
export const purchaseQueueIndicator = z.enum(PURCHASE_QUEUE_INDICATORS);

/**
 * Etapa de uma compra aberta, pela situação gravada. A aprovada se divide em "aguardando contrato ou
 * Jurídico" (o pedido espera o contrato; ADR 0070) e "aguardando pedido". Encerradas: nenhuma.
 */
export function purchaseStageOf(status: PurchaseStatus, waitingContract: boolean) {
  const stages: Partial<Record<PurchaseStatus, PurchaseStage>> = {
    awaiting_need_approval: 'need_approval',
    new: 'triage',
    in_review: 'triage',
    awaiting_info: 'triage',
    quoting: 'quoting',
    awaiting_approval: 'contracting_approval',
    approved: waitingContract ? 'contract' : 'order',
    ordered: 'receiving',
    partially_delivered: 'receiving',
  };
  return stages[status] ?? null;
}

/** O que os filtros precisam saber de cada compra da fila. */
export interface PurchaseQueueRow {
  number: number;
  title: string;
  status: PurchaseStatus;
  priority: string;
  neededBy: string | null;
  departmentId: string | null;
  departmentName: string | null;
  requesterName: string | null;
  buyerId: string | null;
  costCenterId?: string | null;
}

/** Atrasada: aberta e com a data necessária já passada. */
export function isOverduePurchase(
  row: { status: PurchaseStatus; neededBy: string | null },
  today: string,
): boolean {
  return !!row.neededBy && row.neededBy < today && !CLOSED_PURCHASE_STATUSES.includes(row.status);
}

/** Aplica os filtros da fila a uma compra. */
export function matchesQueueFilters(
  row: PurchaseQueueRow,
  filters: Partial<PurchaseQueueFilters>,
  ctx: { userId: string | null; today: string; costCenterIds?: Set<string> | null },
): boolean {
  if (ctx.costCenterIds && (!row.costCenterId || !ctx.costCenterIds.has(row.costCenterId))) {
    return false;
  }
  const owner = filters.owner ?? 'all';
  if (owner === 'me' && row.buyerId !== ctx.userId) return false;
  if (owner === 'none' && row.buyerId) return false;
  if (filters.departmentId && row.departmentId !== filters.departmentId) return false;
  if (filters.priority && row.priority !== filters.priority) return false;
  if (filters.overdue && !isOverduePurchase(row, ctx.today)) return false;
  const term = (filters.search ?? '').trim().toLowerCase();
  if (!term) return true;
  const haystack = [
    purchaseNumber(row.number),
    row.title,
    row.requesterName ?? '',
    row.departmentName ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(term);
}

const label = z.string().trim().min(1).max(120);

/** Exportação da fila em Excel: os mesmos filtros da tela e os textos das colunas no idioma dela. */
export const purchaseQueueExportInput = purchaseQueueFilters.extend({
  status: purchaseQueueStatus,
  indicator: purchaseQueueIndicator.nullish(),
  labels: z.object({
    sheet: label,
    number: label,
    title: label,
    department: label,
    legalEntity: label,
    requester: label,
    buyer: label,
    priority: label,
    status: label,
    neededBy: label,
    overdue: label,
    estimatedAmount: label,
    currency: label,
    items: label,
    createdAt: label,
    yes: label,
    statuses: z.record(z.enum(PURCHASE_STATUSES), label),
    priorities: z.record(z.enum(PURCHASE_PRIORITIES), label),
  }),
});
export type PurchaseQueueExportInput = z.infer<typeof purchaseQueueExportInput>;

/** Exportação do mapa de cotações em Excel. */
export const purchaseComparisonExportInput = z.object({
  labels: z.object({
    sheetItems: label,
    sheetProposals: label,
    supplier: label,
    version: label,
    currency: label,
    item: label,
    description: label,
    quantity: label,
    unit: label,
    unitPrice: label,
    itemTotal: label,
    freight: label,
    total: label,
    convertedTotal: label,
    deliveryDays: label,
    paymentTerms: label,
    validUntil: label,
    lowest: label,
    selected: label,
    yes: label,
  }),
});
export type PurchaseComparisonExportInput = z.infer<typeof purchaseComparisonExportInput>;

/** Período dos indicadores (datas de abertura das solicitações, inclusive). */
export const purchaseIndicatorsQuery = z
  .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  })
  .refine((q) => q.from <= q.to, { message: 'O início precisa ser antes do fim', path: ['from'] });

/** Etapas medidas: onde a compra espera alguém agir. */
export const TRACKED_PURCHASE_STAGES = [
  'awaiting_need_approval',
  'new',
  'in_review',
  'awaiting_info',
  'quoting',
  'awaiting_approval',
  'approved',
] as const satisfies readonly PurchaseStatus[];

export interface IndicatorRequest {
  id: string;
  status: PurchaseStatus;
  createdAt: string;
  closedAt: string | null;
  neededBy: string | null;
  departmentId: string;
  buyerUserId: string | null;
}

export interface IndicatorEvent {
  requestId: string;
  toStatus: PurchaseStatus | null;
  occurredAt: string;
}

export interface PurchaseIndicatorGroup {
  key: string | null;
  created: number;
  open: number;
  closed: number;
}

export interface PurchaseIndicators {
  created: number;
  open: number;
  completed: number;
  rejected: number;
  cancelled: number;
  overdueOpen: number;
  /** Dias, em média, da abertura até a primeira aprovação da contratação. */
  avgDaysToApproval: number | null;
  approvedCount: number;
  /** Dias, em média, da abertura até o encerramento (concluída, recusada ou cancelada). */
  avgDaysToClose: number | null;
  stages: { status: PurchaseStatus; avgDays: number | null; count: number }[];
  byDepartment: PurchaseIndicatorGroup[];
  byBuyer: PurchaseIndicatorGroup[];
}

const days = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / DAY_MS;
const round1 = (n: number) => Math.round(n * 10) / 10;
const average = (values: number[]) =>
  values.length ? round1(values.reduce((a, b) => a + b, 0) / values.length) : null;

/**
 * Indicadores das solicitações abertas no período. O tempo de cada etapa vem da linha do tempo
 * (cada mudança de situação); só contam as passagens já encerradas.
 */
export function purchaseIndicators(
  requests: IndicatorRequest[],
  events: IndicatorEvent[],
  today: string,
): PurchaseIndicators {
  const byRequest = new Map<string, IndicatorEvent[]>();
  for (const e of events) {
    if (!e.toStatus) continue;
    const list = byRequest.get(e.requestId) ?? [];
    list.push(e);
    byRequest.set(e.requestId, list);
  }
  const stageDays = new Map<PurchaseStatus, number[]>();
  const toApproval: number[] = [];
  const toClose: number[] = [];
  const departments = new Map<string | null, PurchaseIndicatorGroup>();
  const buyers = new Map<string | null, PurchaseIndicatorGroup>();
  const tally = (map: Map<string | null, PurchaseIndicatorGroup>, key: string | null) => {
    const g = map.get(key) ?? { key, created: 0, open: 0, closed: 0 };
    map.set(key, g);
    return g;
  };
  let open = 0;
  let overdueOpen = 0;
  const finals = { completed: 0, rejected: 0, cancelled: 0 };

  for (const r of requests) {
    const closed = CLOSED_PURCHASE_STATUSES.includes(r.status);
    if (closed) finals[r.status as keyof typeof finals] += 1;
    else open += 1;
    if (isOverduePurchase(r, today)) overdueOpen += 1;
    for (const g of [tally(departments, r.departmentId), tally(buyers, r.buyerUserId)]) {
      g.created += 1;
      if (closed) g.closed += 1;
      else g.open += 1;
    }
    if (closed && r.closedAt) toClose.push(days(r.createdAt, r.closedAt));

    const timeline = (byRequest.get(r.id) ?? []).toSorted((a, b) =>
      a.occurredAt.localeCompare(b.occurredAt),
    );
    const approved = timeline.find((e) => e.toStatus === 'approved');
    if (approved) toApproval.push(days(r.createdAt, approved.occurredAt));
    for (const [i, e] of timeline.entries()) {
      const next = timeline[i + 1];
      const status = e.toStatus!;
      if (!next || !(TRACKED_PURCHASE_STAGES as readonly string[]).includes(status)) continue;
      const list = stageDays.get(status) ?? [];
      list.push(days(e.occurredAt, next.occurredAt));
      stageDays.set(status, list);
    }
  }

  const sortGroups = (groups: Iterable<PurchaseIndicatorGroup>) =>
    [...groups].toSorted(
      (a, b) => b.created - a.created || String(a.key).localeCompare(String(b.key)),
    );

  return {
    created: requests.length,
    open,
    ...finals,
    overdueOpen,
    avgDaysToApproval: average(toApproval),
    approvedCount: toApproval.length,
    avgDaysToClose: average(toClose),
    stages: TRACKED_PURCHASE_STAGES.map((status) => {
      const list = stageDays.get(status) ?? [];
      return { status, avgDays: average(list), count: list.length };
    }),
    byDepartment: sortGroups(departments.values()),
    byBuyer: sortGroups(buyers.values()),
  };
}

// ----- Indicadores da Visão geral de Compras -----

/** Situações em que a compra espera uma aprovação, com o tipo de aprovação de cada uma. */
export const PURCHASE_APPROVAL_STATUSES = {
  awaiting_need_approval: 'purchasing.need',
  awaiting_approval: 'purchasing.contracting',
} as const;

export type PurchaseDeadlineBucket = 'in' | 'out' | 'none';

/** Atendimento pelo farol do SLA: vermelho é fora do prazo; sem meta, sem prazo; o resto, no prazo. */
export function attendanceBucket(light: SlaLight): PurchaseDeadlineBucket {
  if (light === 'red') return 'out';
  return light === 'none' ? 'none' : 'in';
}

/**
 * Aprovação pelo prazo da etapa atual (o mesmo "vencida" da caixa de aprovações). Sem etapa aberta
 * ou sem prazo cadastrado, não há prazo a medir. Etapas em paralelo: vale a que vence primeiro.
 */
export function approvalBucket(
  dueAts: readonly (number | null)[],
  now: number,
): PurchaseDeadlineBucket {
  const dues = dueAts.filter((d): d is number => d !== null);
  if (dues.length === 0) return 'none';
  return Math.min(...dues) <= now ? 'out' : 'in';
}

/** Documento obrigatório que ainda não vale: o anexado fica "em revisão" até ser aprovado. */
export const PENDING_REQUIRED_DOCUMENT_SITUATIONS = [
  'missing',
  'requested',
  'in_review',
  'rejected',
  'expired',
] as const;
export type PendingRequiredDocumentSituation =
  (typeof PENDING_REQUIRED_DOCUMENT_SITUATIONS)[number];

export function isPendingRequiredDocument(doc: { required: boolean; situation: string }): boolean {
  return (
    doc.required &&
    (PENDING_REQUIRED_DOCUMENT_SITUATIONS as readonly string[]).includes(doc.situation)
  );
}
